import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ImageIcon, Building2, Lock, Mail, Phone, User, Shield, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import LogoUpload from './LogoUpload';
import ClubInfoForm from './ClubInfoForm';
import PasswordChangeModal from './PasswordChangeModal';
import EmailConfigForm from './EmailConfigForm';
import { useAuth } from '../contexts/AuthContext';

const ClubSettings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button 
            variant="outline" 
            onClick={() => navigate('/dashboard')}
            className="flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au dashboard
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">
              Paramètres du club
            </h1>
            <p className="text-gray-600">
              {user?.name} • Gérez les paramètres de votre club
            </p>
          </div>
        </div>

        {/* Settings Sections */}
        <div className="space-y-8">
          
          {/* Section 1: Club Identity - 2 columns */}
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Identité du club
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Club Information Card */}
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    Informations générales
                  </CardTitle>
                  <CardDescription className="text-sm">
                    Nom d'affichage et coordonnées
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ClubInfoForm />
                </CardContent>
              </Card>

              {/* Logo Card */}
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <ImageIcon className="w-5 h-5 text-purple-600" />
                    Logo du club
                  </CardTitle>
                  <CardDescription className="text-sm">
                    Personnalisez votre identité visuelle
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <LogoUpload />
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Section 2: Security & Communications - 2 columns */}
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-600" />
              Sécurité et communications
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Security Card */}
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Shield className="w-5 h-5 text-red-600" />
                    Sécurité du compte
                  </CardTitle>
                  <CardDescription className="text-sm">
                    Gestion du mot de passe de connexion
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                      <Lock className="w-5 h-5 text-gray-600" />
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-800">Mot de passe</h4>
                        <p className="text-sm text-gray-600">Dernière modification : non disponible</p>
                      </div>
                      <Button
                        onClick={() => setIsPasswordModalOpen(true)}
                        variant="outline"
                        size="sm"
                        className="border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <Lock className="w-4 h-4 mr-2" />
                        Changer
                      </Button>
                    </div>
                    
                    <div className="text-xs text-gray-500 bg-blue-50 p-3 rounded-lg">
                      <p className="font-medium text-blue-800 mb-1">💡 Conseils de sécurité</p>
                      <p>Changez régulièrement votre mot de passe et utilisez une combinaison sécurisée.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions Card */}
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <User className="w-5 h-5 text-green-600" />
                    Actions rapides
                  </CardTitle>
                  <CardDescription className="text-sm">
                    Raccourcis vers les fonctionnalités principales
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      onClick={() => navigate('/members')}
                      className="flex flex-col items-center gap-2 h-auto py-4 border-green-200 hover:bg-green-50"
                    >
                      <User className="w-5 h-5 text-green-600" />
                      <span className="text-sm">Membres</span>
                    </Button>
                    
                    <Button
                      variant="outline"
                      onClick={() => navigate('/dashboard')}
                      className="flex flex-col items-center gap-2 h-auto py-4 border-blue-200 hover:bg-blue-50"
                    >
                      <FileText className="w-5 h-5 text-blue-600" />
                      <span className="text-sm">PVs</span>
                    </Button>
                    
                    <Button
                      variant="outline"
                      onClick={() => window.open('mailto:support@emergent.host', '_blank')}
                      className="flex flex-col items-center gap-2 h-auto py-4 col-span-2 border-gray-200 hover:bg-gray-50"
                    >
                      <Mail className="w-5 h-5 text-gray-600" />
                      <span className="text-sm">Support technique</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Section 3: Email Configuration - Full width */}
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Configuration des emails
            </h2>
            <EmailConfigForm />
          </div>

        </div>

        <PasswordChangeModal 
          isOpen={isPasswordModalOpen}
          onClose={() => setIsPasswordModalOpen(false)}
        />
      </div>
    </div>
  );
};

export default ClubSettings;